import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { isLoggedIn, logoutUser } from "../helpers/authHelper";
import UserAvatar from "./UserAvatar";

import {
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

const Navbar = () => {
  const user = isLoggedIn();
  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");

  const handleLogout = () => {
    logoutUser();
    navigate("/login");
    setMenuOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    navigate(`/search?query=${encodeURIComponent(query)}`);
    setQuery("");
    setMenuOpen(false);
  };

  const isActive = (path) =>
    location.pathname === path
      ? "text-violet-600 font-semibold"
      : "text-gray-600 hover:text-violet-600";

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* LEFT */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition"
            >
              {menuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>

            <Link to="/" className="font-semibold text-lg">
              <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                🫟 FLOWLINE
              </span>
            </Link>

            {/* DESKTOP NAV (Logged In Only) */}
            {user && (
              <nav className="hidden md:flex items-center gap-4 text-sm">
                <Link to="/journal" className={isActive("/journal")}>
                  Journal
                </Link>
                <Link to="/messenger" className={isActive("/messenger")}>
                  Messages
                </Link>
                <Link to="/games" className={isActive("/games")}>
                  Games
                </Link>
              </nav>
            )}
          </div>

          {/* CENTER SEARCH */}
          <form
            onSubmit={handleSearch}
            className="hidden md:flex flex-1 justify-center"
          >
            <div className="relative w-full max-w-xl">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search posts, people, topics…"
                className="w-full pl-10 pr-4 py-2 rounded-xl border bg-gray-50
                           focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </form>

          {/* RIGHT */}
          <div className="flex items-center gap-4">
            <Link
              to="/about"
              className={`hidden md:inline text-sm ${isActive("/about")}`}
            >
              About
            </Link>

            {user ? (
              <>
                <Link
                  to="/posts/create"
                  className="hidden md:inline-flex px-4 py-2 rounded-xl
                             font-semibold text-white
                             bg-gradient-to-r from-violet-600 to-purple-600
                             hover:opacity-90 transition"
                >
                  Write
                </Link>

                {/* DESKTOP AVATAR + LOGOUT */}
                <div className="hidden md:flex items-center gap-3">
                  <UserAvatar username={user.username} width={36} height={36} />
                  <button
                    onClick={handleLogout}
                    className="text-sm font-medium text-gray-600 hover:text-red-600 transition"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium text-gray-600 hover:text-violet-600"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 rounded-xl font-semibold text-white
                             bg-gradient-to-r from-violet-600 to-purple-600"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* MOBILE MENU */}
      {menuOpen && (
        <div className="md:hidden absolute top-16 inset-x-0 z-40">
          <div className="mx-4 mt-2 rounded-2xl bg-white shadow-xl border p-4 space-y-4">
            <form onSubmit={handleSearch}>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full px-4 py-2 rounded-xl border bg-gray-50
                           focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </form>

            <nav className="flex flex-col text-sm">
              <Link
                onClick={() => setMenuOpen(false)}
                to="/"
                className="menu-item"
              >
                Home
              </Link>

              {user && (
                <>
                  <Link
                    onClick={() => setMenuOpen(false)}
                    to="/journal"
                    className="menu-item"
                  >
                    Journal
                  </Link>
                  <Link
                    onClick={() => setMenuOpen(false)}
                    to="/messenger"
                    className="menu-item"
                  >
                    Messages
                  </Link>
                  <Link
                    onClick={() => setMenuOpen(false)}
                    to="/games"
                    className="menu-item"
                  >
                    Games
                  </Link>
                </>
              )}

              <Link
                onClick={() => setMenuOpen(false)}
                to="/about"
                className="menu-item"
              >
                About
              </Link>

              <div className="border-t mt-2 pt-2">
                {user ? (
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2 rounded-lg
                               text-red-600 hover:bg-red-50"
                  >
                    Logout
                  </button>
                ) : (
                  <>
                    <Link to="/login" className="menu-item">
                      Sign In
                    </Link>
                    <Link to="/signup" className="menu-item">
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        </div>
      )}

      <style>{`
        .menu-item {
          padding: 0.5rem 0.75rem;
          border-radius: 0.5rem;
        }
        .menu-item:hover {
          background: #f3f4f6;
        }
      `}</style>
    </header>
  );
};

export default Navbar;
